import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'
import type {
  AgentAttendanceSettings,
  AgentAttendanceSettingsInput,
  AgentDetail,
  AgentInput,
  AgentListItem,
  AgentTestMessage,
  AgentTestResponse,
  PendingBookingConfirmation,
} from '@/types/agents'

export const agentsKeys = {
  list: (orgId: string | null) => ['agents', 'list', orgId] as const,
  detail: (orgId: string | null, agentId: string | null) =>
    ['agents', 'detail', orgId, agentId] as const,
  attendanceSettings: (orgId: string | null, agentId: string | null) =>
    ['agents', 'attendance-settings', orgId, agentId] as const,
}

export function useAgentAttendanceSettings(
  orgId: string | null,
  agentId: string | null,
  enabled = true
) {
  return useQuery<AgentAttendanceSettings | null>({
    queryKey: agentsKeys.attendanceSettings(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<AgentAttendanceSettings | null>(
        `/orgs/${orgId}/agents/${agentId}/attendance-settings`
      )
      return res.data
    },
    enabled: !!orgId && !!agentId && enabled,
    staleTime: 30_000,
  })
}

export function useUpsertAgentAttendanceSettings(
  orgId: string | null,
  agentId: string
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: AgentAttendanceSettingsInput) => {
      const res = await api.put<AgentAttendanceSettings>(
        `/orgs/${orgId}/agents/${agentId}/attendance-settings`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: agentsKeys.attendanceSettings(orgId, agentId),
      })
    },
  })
}

export function useAgents(orgId: string | null) {
  return useQuery<PaginatedResponse<AgentListItem>>({
    queryKey: agentsKeys.list(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<AgentListItem>>(
        `/orgs/${orgId}/agents`,
        { params: { limit: 100 } }
      )
      return res.data
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useAgentDetail(orgId: string | null, agentId: string | null) {
  return useQuery<AgentDetail>({
    queryKey: agentsKeys.detail(orgId, agentId),
    queryFn: async () => {
      const res = await api.get<AgentDetail>(`/orgs/${orgId}/agents/${agentId}`)
      return res.data
    },
    enabled: !!orgId && !!agentId,
    staleTime: 30_000,
  })
}

export function useCreateAgent(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: AgentInput) => {
      const res = await api.post<AgentDetail>(`/orgs/${orgId}/agents`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentsKeys.list(orgId) })
    },
  })
}

export function useUpdateAgent(orgId: string | null, agentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: AgentInput) => {
      const res = await api.put<AgentDetail>(
        `/orgs/${orgId}/agents/${agentId}`,
        data
      )
      return res.data
    },
    onSuccess: (data) => {
      // Atualiza o cache do detail com a resposta autoritativa do servidor em vez
      // de invalidar (evita refetch a cada auto-save). Só a lista é invalidada.
      qc.setQueryData(agentsKeys.detail(orgId, agentId), data)
      void qc.invalidateQueries({ queryKey: agentsKeys.list(orgId) })
    },
  })
}

export function useDeleteAgent(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (agentId: string) => {
      await api.delete(`/orgs/${orgId}/agents/${agentId}`)
      return agentId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentsKeys.list(orgId) })
    },
  })
}

export function useDuplicateAgent(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await api.post<AgentDetail>(
        `/orgs/${orgId}/agents/${agentId}/duplicate`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentsKeys.list(orgId) })
    },
  })
}

export function useTestAgent(orgId: string | null, agentId: string | null) {
  return useMutation({
    mutationFn: async (params: {
      messages: AgentTestMessage[]
      contactId?: string
      simulatedFields?: Array<{ field: string; value: string }>
      pendingBooking?: PendingBookingConfirmation | null
    }) => {
      const res = await api.post<AgentTestResponse>(
        `/orgs/${orgId}/agents/${agentId}/test`,
        {
          messages: params.messages,
          contactId: params.contactId,
          simulatedFields: params.simulatedFields,
          pendingBooking: params.pendingBooking,
        }
      )
      return res.data
    },
  })
}

export function useTranscribeAgentTestAudio(
  orgId: string | null,
  agentId: string | null
) {
  return useMutation({
    mutationFn: async (file: File) => {
      const extension = file.name.toLowerCase().split('.').pop()
      const normalizedFile =
        extension === 'm4a' && !file.type.startsWith('audio/')
          ? new File([file], file.name, {
              type: 'audio/mp4',
              lastModified: file.lastModified,
            })
          : file
      const formData = new FormData()
      formData.append('file', normalizedFile, normalizedFile.name)
      const res = await api.post<{ transcription: string }>(
        `/orgs/${orgId}/agents/${agentId}/test/audio-transcription`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return res.data
    },
  })
}
